import React from "react";
import { Badge } from "../../../../components/badge";
import { Button } from "../../../../components/button";
import { CheckCircle2, PauseCircle, Trash2 } from "lucide-react";
import type { TFunction } from "i18next";
import type { DataTableColumn, DataTableOption } from "../../../../components/table";
import type { MerchantFilters, MerchantListItem, MerchantStatus } from "../../types/merchant.types";
import {
  formatMerchantDate,
  getMerchantDisplayName,
  getMerchantPartnerTypeTranslationKey,
  getMerchantStatus,
} from "../../utils/merchant.utils";

type BuildMerchantListColumnsParams = {
  t: TFunction;
  filters: MerchantFilters;
  updateFilter: React.Dispatch<React.SetStateAction<MerchantFilters>>;
  categoryOptions: DataTableOption[];
  verificationOptions: DataTableOption[];
  badgeOptions: DataTableOption[];
  dateSortDirection?: "asc" | "desc" | null;
  onDateSort?: (nextDirection: "asc" | "desc" | null) => void;
  onRowOpen: (merchant: MerchantListItem) => void;
  onApprove: (merchant: MerchantListItem) => void;
  onSuspend: (merchant: MerchantListItem) => void;
  onDelete: (merchant: MerchantListItem) => void;
};

const getMerchantStatusColor = (status: MerchantStatus) => {
  switch (status) {
    case "verified":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    case "suspended":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
    case "pending":
    default:
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
  }
};

const MerchantActionsCell = ({
  merchant,
  status,
  onApprove,
  onSuspend,
  onDelete,
}: {
  merchant: MerchantListItem;
  status: MerchantStatus;
  onApprove: (merchant: MerchantListItem) => void;
  onSuspend: (merchant: MerchantListItem) => void;
  onDelete: (merchant: MerchantListItem) => void;
}) => (
  <div className="flex gap-2">
    {status === "pending" ? (
      <Button
        size="sm"
        variant="outline"
        className="h-8 w-8 rounded-md border border-gray-300 bg-white p-0 text-emerald-600 hover:bg-gray-50 hover:text-emerald-700 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
        onClick={(e) => {
          e.stopPropagation();
          onApprove(merchant);
        }}
        aria-label="Approve merchant"
      >
        <CheckCircle2 className="h-4 w-4" />
      </Button>
    ) : null}
    {status !== "suspended" ? (
      <Button
        size="sm"
        variant="outline"
        className="h-8 w-8 rounded-md border border-gray-300 bg-white p-0 text-amber-600 hover:bg-gray-50 hover:text-amber-700 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
        onClick={(e) => {
          e.stopPropagation();
          onSuspend(merchant);
        }}
        aria-label="Suspend merchant"
      >
        <PauseCircle className="h-4 w-4" />
      </Button>
    ) : null}
    <Button
      size="sm"
      variant="outline"
      className="h-8 w-8 rounded-md border border-gray-300 bg-white p-0 text-red-600 hover:bg-gray-50 hover:text-red-700 dark:border-gray-600 dark:bg-gray-800 dark:text-red-600 dark:hover:text-red-700"
      onClick={(e) => {
        e.stopPropagation();
        onDelete(merchant);
      }}
      aria-label="Delete merchant"
    >
      <Trash2 className="h-4 w-4" />
    </Button>
  </div>
);

export const buildMerchantListColumns = ({
  t,
  filters,
  updateFilter,
  categoryOptions,
  verificationOptions,
  badgeOptions,
  dateSortDirection,
  onDateSort,
  onApprove,
  onSuspend,
  onDelete,
}: BuildMerchantListColumnsParams): DataTableColumn<MerchantListItem>[] => [
  {
    key: "business",
    title: t("merchant.business_name"),
    filter: {
      type: "string",
      value: filters.keyword,
      onFilter: (value: string) =>
        updateFilter((prev) => ({
          ...prev,
          keyword: value,
        })),
      placeholder: t("merchant.search_merchants"),
      title: t("merchant.filter_by_keyword"),
      description: "Press Enter to search",
      submitMode: "enter",
    },
    render: (merchant) => (
      <div className="flex items-center gap-3">
        {merchant.logo_url ? (
          <img
            src={merchant.logo_url}
            alt={getMerchantDisplayName(merchant)}
            className="h-10 w-10 rounded-lg object-cover"
          />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-sm font-bold text-gray-500">
            {(merchant.business_name || merchant.email || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <div className="max-w-[200px]">
          <div
            className="truncate text-sm font-semibold"
            title={merchant.business_name || ""}
          >
            {getMerchantDisplayName(merchant)}
          </div>
          <div
            className="truncate text-xs text-gray-500 dark:text-gray-400"
            title={merchant.email || ""}
          >
            {merchant.email || "—"}
          </div>
        </div>
      </div>
    ),
    cellClassName: "w-64",
  },
  {
    key: "phone",
    title: t("merchant.phone"),
    render: (merchant) => <span className="text-xs">{merchant.phone || "—"}</span>,
    cellClassName: "w-32",
  },
  {
    key: "category",
    title: t("common.category"),
    filter: {
      type: "dropdown",
      value: filters.category_id,
      onFilter: (value: string) =>
        updateFilter((prev) => ({
          ...prev,
          category_id: value,
        })),
      options: categoryOptions,
      title: t("merchant.filter_by_category"),
      placeholder: t("merchant.select_category"),
      applyLabel: t("common.apply"),
      clearLabel: t("common.clear"),
    },
    render: (merchant) => <span className="text-xs">{merchant.category_name || "—"}</span>,
    cellClassName: "w-36",
    align: "center",
  },
  {
    key: "partner_type",
    title: t("merchant.community_partner_type"),
    render: (merchant) => {
      const translationKey = getMerchantPartnerTypeTranslationKey(merchant.partner_type);

      return (
        <span className="text-xs">
          {translationKey ? t(translationKey) : merchant.partner_type || "—"}
        </span>
      );
    },
    cellClassName: "w-48",
  },
  {
    key: "verification_status",
    title: t("merchant.verification_status"),
    filter: {
      type: "dropdown",
      value: filters.verification_status,
      onFilter: (value: string) =>
        updateFilter((prev) => ({
          ...prev,
          verification_status: value === "verified" || value === "pending" ? value : "",
        })),
      options: verificationOptions,
      title: t("merchant.filter_by_status"),
      placeholder: t("merchant.select_status"),
      applyLabel: t("common.apply"),
      clearLabel: t("common.clear"),
    },
    render: (merchant) => {
      const status = getMerchantStatus(merchant);
      const label =
        status === "verified"
          ? t("merchant.verified")
          : status === "suspended"
            ? t("merchant.suspended")
            : t("merchant.pending");
      return <Badge className={getMerchantStatusColor(status)}>{label}</Badge>;
    },
    align: "center",
  },
  {
    key: "badge_level",
    title: t("merchant.badge_level"),
    filter: {
      type: "dropdown",
      value: filters.badge_level,
      onFilter: (value: string) =>
        updateFilter((prev) => ({
          ...prev,
          badge_level: value,
        })),
      options: badgeOptions,
      title: t("merchant.filter_by_badge"),
      placeholder: t("merchant.select_badge"),
      applyLabel: t("common.apply"),
      clearLabel: t("common.clear"),
    },
    render: (merchant) => <span className="text-xs font-medium">{merchant.badge_level}</span>,
    align: "center",
  },
  {
    key: "created_at",
    title: t("merchant.created_at"),
    sort: onDateSort
      ? {
          direction: dateSortDirection ?? null,
          onSort: onDateSort,
          title: t("merchant.created_at"),
        }
      : undefined,
    render: (merchant) => (
      <span className="text-xs">{formatMerchantDate(merchant.created_at)}</span>
    ),
  },
  {
    key: "actions",
    title: t("common.actions"),
    render: (merchant) => (
      <MerchantActionsCell
        merchant={merchant}
        status={getMerchantStatus(merchant)}
        onApprove={onApprove}
        onSuspend={onSuspend}
        onDelete={onDelete}
      />
    ),
    align: "center",
    cellClassName: "w-36",
  },
];
